import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, Button, Input, Statistic, Table, Tag, message, Spin, Empty, Space, Divider, Alert } from 'antd';
import { ShareAltOutlined, GiftOutlined, UserAddOutlined, CopyOutlined, DollarOutlined, TeamOutlined, LinkOutlined, StarFilled } from '@ant-design/icons';
import { referralService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './CustomerDashboard.css';

const { Title, Text, Paragraph } = Typography;

const Referral = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [referralInfo, setReferralInfo] = useState(null);
  const [stats, setStats] = useState(null);
  const [earnings, setEarnings] = useState([]);
  const [referralCode, setReferralCode] = useState('');
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [infoRes, statsRes] = await Promise.all([
        referralService.getReferralInfo().catch(() => ({ data: {} })),
        referralService.getReferralStats().catch(() => ({ data: {} })),
      ]);
      const infoData = infoRes.data?.data || infoRes.data || {};
      const statsData = statsRes.data?.data || statsRes.data || {};
      setReferralInfo(infoData);
      setStats(statsData);
      setReferralCode(infoData.referral_code || statsData.referral_code || user?.referral_code || '');
    } catch (err) {
      console.error('Failed to fetch referral data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    const shareUrl = `${window.location.origin}/?ref=${referralCode}`;
    navigator.clipboard.writeText(shareUrl);
    message.success('Referral link copied!');
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      const shareUrl = `${window.location.origin}/?ref=${referralCode}`;
      if (navigator.share) {
        await navigator.share({ title: 'Join Current Care', text: `Use my referral code ${referralCode} to get started!`, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(`${shareUrl} | Code: ${referralCode}`);
        message.success('Referral info copied to clipboard!');
      }
    } catch { /* user cancelled */ }
    finally { setSharing(false); }
  };

  if (loading) return <div className="customer-page" style={{ padding: 0, textAlign: 'center', paddingTop: 80 }}><Spin size="large" /></div>;

  const bonusAmount = referralInfo?.bonus_amount || stats?.bonus_amount || 0;
  const totalReferrals = stats?.total_referrals || referralInfo?.total_referrals || 0;
  const totalEarnings = stats?.total_earnings || referralInfo?.total_earnings || 0;

  return (
    <div className="customer-page" style={{ padding: 0 }}>
      <div className="customer-page-header">
        <Title level={2}>Referral Program</Title>
        <Paragraph>Invite your friends and earn rewards when they join Current Care!</Paragraph>
      </div>

      {referralCode && (
        <Alert
          message="Your Referral Code"
          description={
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <Text code style={{ fontSize: 20, letterSpacing: 3, padding: '4px 16px' }}>{referralCode}</Text>
              <Button icon={<CopyOutlined />} onClick={handleCopy}>Copy Code</Button>
              <Button icon={<ShareAltOutlined />} onClick={handleShare} loading={sharing}>Share</Button>
            </div>
          }
          type="success"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card className="customer-card"><Statistic title="Total Referrals" value={totalReferrals} prefix={<TeamOutlined />} valueStyle={{ color: '#2d5a3d' }} /></Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card className="customer-card"><Statistic title="Total Earnings" value={totalEarnings} prefix={<DollarOutlined />} precision={2} valueStyle={{ color: '#52b788' }} suffix="NPR" /></Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card className="customer-card"><Statistic title="Bonus per Referral" value={bonusAmount} prefix={<GiftOutlined />} precision={2} valueStyle={{ color: '#f1a134' }} suffix="NPR" /></Card>
        </Col>
      </Row>

      <Card title="How It Works" style={{ marginTop: 16 }} className="customer-card">
        <Row gutter={[24, 24]}>
          <Col xs={24} md={8} style={{ textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#e8f5e9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
              <LinkOutlined style={{ fontSize: 24, color: '#52b788' }} />
            </div>
            <Title level={5}>Share Your Code</Title>
            <Text type="secondary">Share your unique referral link with friends and family</Text>
          </Col>
          <Col xs={24} md={8} style={{ textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#fef4e3', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
              <UserAddOutlined style={{ fontSize: 24, color: '#f1a134' }} />
            </div>
            <Title level={5}>They Sign Up</Title>
            <Text type="secondary">Your friend signs up and places their first order</Text>
          </Col>
          <Col xs={24} md={8} style={{ textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#e8f5e9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
              <StarFilled style={{ fontSize: 24, color: '#f1a134' }} />
            </div>
            <Title level={5}>Earn Rewards</Title>
            <Text type="secondary">You earn NPR {bonusAmount} for every successful referral!</Text>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default Referral;
